import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaSpinner, FaCheck, FaRegCircle } from 'react-icons/fa';
import { IGlobalState } from '../model/IGlobalState';
import api from '../api/api';
import { SetUser } from '../actions/SetUser';
import BrandPanel from './BrandPanel';

// mirrors HashHelpers.ValidatePassword
const rules: [string, (p: string) => boolean][] = [
    ['At least 8 characters', p => p.length >= 8],
    ['A letter', p => p.split('').some(c => c.toLowerCase() !== c.toUpperCase())],
    ['A number', p => /\d/.test(p)],
];
const strengthLabels = ['', 'Weak password', 'Fair password', 'Strong password'];

// Two modes: emailed link (/reset-password?u=&t=, signed out) or forced reset after an admin-emailed temporary password (signed in)
const ResetPasswordPage = () => {
    const params = new URLSearchParams(window.location.search);
    const userId = params.get('u') || '';
    const token = params.get('t') || '';
    const linkMode = !!token;

    const signedInAs = useSelector((state: IGlobalState) => state.user.username);
    const dispatch = useDispatch();
    const [ email, setEmail ] = useState(linkMode ? '' : signedInAs);
    const [ linkError, setLinkError ] = useState('');
    // inputs stay uncontrolled: Chrome's "use strong password" fill gets wiped by a controlled re-render
    const passwordRef = useRef<HTMLInputElement>(null);
    const [ password, setPassword ] = useState('');
    const [ confirm, setConfirm ] = useState('');
    const [ showPassword, setShowPassword ] = useState(false);
    const [ showConfirm, setShowConfirm ] = useState(false);
    const [ error, setError ] = useState('');
    const [ loading, setLoading ] = useState(false);

    useEffect(() => {
        // signed out with no token (e.g. a link cut short in the email): say so instead of a blank page
        if (!linkMode && !signedInAs) { setLinkError('This reset link is incomplete'); return; }
        if (!linkMode) return;
        api().getPasswordReset(userId, token)
            .then(r => setEmail(r.email))
            .catch(err => setLinkError(err?.response?.data || 'This reset link is not valid'));
    }, []);

    const met = rules.filter(([, test]) => test(password)).length;
    const mismatch = confirm.length > 0 && confirm !== password;
    const canSave = met === rules.length && confirm === password && !loading;

    const save = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const value = passwordRef.current?.value || '';
        try {
            if (linkMode) {
                await api().resetPassword(userId, token, value);
            } else {
                const result = await api().changePassword(value);
                dispatch(SetUser(result.user));
            }
            window.location.href = '/';
        } catch (err: any) {
            setError(err?.response?.data || 'Could not change password, please try again');
            setLoading(false);
        }
    };

    return <div className="login-page reset-password-page">
        <BrandPanel />
        <div className="form-panel">
            { linkError && <div className="login-box">
                <div className="heading">
                    <h2>Link expired</h2>
                    <p>{linkError}. Reset links last 30 minutes and work once.</p>
                </div>
                <Link to="/forgot-password" className="button-link">Request a new link</Link>
            </div> }
            { !linkError && email && <form className="login-box" onSubmit={save}>
                <div className="heading">
                    <h2>Set a new password</h2>
                    <p>{linkMode ? 'You\'re resetting your password.' : 'You need to choose a new password before continuing.'} Choose something you don't use anywhere else.</p>
                </div>
                {/* visible + normal size: Chrome ignores hidden/off-screen fields when pairing username with the saved password */}
                <div className="field">
                    <label htmlFor="reset-username">Email address</label>
                    <div className="input-row">
                        <input id="reset-username" type="email" className="username-readonly" autoComplete="username" value={email} readOnly tabIndex={-1} />
                    </div>
                </div>
                <div className="field">
                    <label htmlFor="reset-password">New password</label>
                    <div className="input-row">
                        <input id="reset-password" ref={passwordRef} type={showPassword ? 'text' : 'password'} autoComplete="new-password" autoFocus
                            onChange={e => setPassword(e.target.value)} disabled={loading} />
                        <button type="button" className="show-toggle" tabIndex={-1} onClick={() => setShowPassword(!showPassword)}>{showPassword ? 'Hide' : 'Show'}</button>
                    </div>
                    <div className="strength">
                        <div className="bars">{rules.map((_, i) => <span key={i} className={i < met ? 'on' : ''} />)}</div>
                        <div className="strength-row">
                            <span className={met === rules.length ? 'ok' : ''}>{password ? strengthLabels[met] : ''}</span>
                            <span>{password.length} characters</span>
                        </div>
                        <div className="chips">
                            {rules.map(([label, test]) => {
                                const ok = test(password);
                                return <span key={label} className={'chip' + (ok ? ' ok' : '')}>{ok ? <FaCheck size={9} /> : <FaRegCircle size={8} />} {label}</span>;
                            })}
                        </div>
                    </div>
                </div>
                <div className="field">
                    <label htmlFor="reset-confirm">Confirm new password</label>
                    <div className={'input-row' + (mismatch ? ' invalid' : '')}>
                        <input id="reset-confirm" type={showConfirm ? 'text' : 'password'} autoComplete="new-password"
                            onChange={e => setConfirm(e.target.value)} disabled={loading} />
                        <button type="button" className="show-toggle" tabIndex={-1} onClick={() => setShowConfirm(!showConfirm)}>{showConfirm ? 'Hide' : 'Show'}</button>
                    </div>
                    {mismatch && <p className="field-error">Passwords don't match yet</p>}
                </div>
                {error && <p className="error">{error}</p>}
                <button type="submit" disabled={!canSave}>Update Password &nbsp;
                    { loading && <FaSpinner className="fa-spin" size={16} color="#0b1739" />}
                </button>
                <div className="help">
                    { linkMode
                        ? <><span>Link expired?</span> <Link to="/forgot-password">Request a new one</Link></>
                        : <><span>Need help?</span> <a href="mailto:contact@investi.com.au">Contact support</a></> }
                </div>
            </form> }
        </div>
    </div>;
};

export default ResetPasswordPage;
